import type { CapturedBy, InterpretationItem, MarkGeometry, UiElement, UiInterpretation, UiMark, UiRequest, UiRequestSummary, UiOp, Viewport } from '@civil/schema';
import { apiFetch } from './project.js';

/**
 * The UI tab's calls (docs/ui-tab.md). Every change answers with `effect`, what it did
 * in words, which the tab shows as it is (CLAUDE.md, agent first).
 */

export interface PersonalLink {
  frontend: string;
  name: string;
  url: string;
}

export interface UiState {
  personalLinks: PersonalLink[];
  requests: UiRequestSummary[];
  /** `memory` locally: images do not survive the API restarting. */
  imageStore: 'gcs' | 'memory';
}

async function call<T>(method: string, url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(url, {
    method,
    signal: signal ?? null,
    headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const parsed = (await response.json().catch(() => ({}))) as T & { message?: string; error?: string };
  if (!response.ok) throw new Error(parsed.message ?? parsed.error ?? `${response.status} ${response.statusText}`);
  return parsed;
}

const base = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/ui`;

export const fetchUiState = (projectId: string, signal?: AbortSignal) => call<UiState>('GET', base(projectId), undefined, signal);

export const fetchRequest = (projectId: string, requestId: string, signal?: AbortSignal) =>
  call<{ request: UiRequest }>('GET', `${base(projectId)}/requests/${requestId}`, undefined, signal).then((r) => r.request);

export const createRequest = (projectId: string, frontend: string, title = '') =>
  call<{ request: UiRequest; effect: string }>('POST', `${base(projectId)}/requests`, { frontend, title });

export const renameRequest = (projectId: string, requestId: string, title: string) =>
  call<{ effect: string }>('PATCH', `${base(projectId)}/requests/${requestId}`, { title });

/** Matches every mark in the request to the code (docs/ui-tab.md §6): minutes at most, usually seconds. */
export const matchRequest = (projectId: string, requestId: string) =>
  call<{ request: UiRequest; effect: string; notes: string[] }>('POST', `${base(projectId)}/requests/${requestId}/match`, {});

/** Interprets the request (docs/ui-tab.md §7), with each snapshot drawn as the tab shows it, by snapshot id. */
export const interpretRequest = (projectId: string, requestId: string, composites: Record<string, string>) =>
  call<{ request: UiRequest; interpretation: UiInterpretation; effect: string; notes: string[] }>(
    'POST',
    `${base(projectId)}/requests/${requestId}/interpret`,
    { composites },
  );

export const correctInterpretation = (projectId: string, requestId: string, id: string, items: InterpretationItem[]) =>
  call<{ effect: string }>('PATCH', `${base(projectId)}/requests/${requestId}/interpretation`, { id, items });

/** The request as one prompt for any agent, and the images it names (docs/ui-tab.md §8). */
export const exportRequest = (projectId: string, requestId: string) =>
  call<{ markdown: string; images: { name: string; snapshot: string; image: string; width: number; height: number }[] }>(
    'GET',
    `${base(projectId)}/requests/${requestId}/export`,
  );

export const deleteRequest = (projectId: string, requestId: string) =>
  call<{ effect: string }>('DELETE', `${base(projectId)}/requests/${requestId}`);

export const addSnapshot = (
  projectId: string,
  requestId: string,
  input: { image: string; url: string; viewport?: Viewport; capturedBy?: CapturedBy; elements?: UiElement[] },
) =>
  call<{ snapshot: { id: string; position: number; width: number; height: number; image: string }; effect: string }>(
    'POST',
    `${base(projectId)}/requests/${requestId}/snapshots`,
    { capturedBy: 'upload', ...input },
  );

export const deleteSnapshot = (projectId: string, snapshotId: string) =>
  call<{ effect: string }>('DELETE', `${base(projectId)}/snapshots/${snapshotId}`);

export const addMark = (projectId: string, snapshotId: string, mark: { geometry: MarkGeometry; note?: string; element?: number }) =>
  call<{ mark: UiMark; effect: string }>('POST', `${base(projectId)}/snapshots/${snapshotId}/marks`, mark);

export const updateMark = (projectId: string, markId: string, patch: { note?: string; geometry?: MarkGeometry }) =>
  call<{ effect: string }>('PATCH', `${base(projectId)}/marks/${markId}`, patch);

export const deleteMark = (projectId: string, markId: string) =>
  call<{ effect: string }>('DELETE', `${base(projectId)}/marks/${markId}`);

export const applyUiOps = (projectId: string, ops: UiOp[]) =>
  call<{ changed: boolean; effect: string }>('POST', `${base(projectId)}/ops`, { ops });

export const setPersonalLink = (projectId: string, link: PersonalLink) =>
  call<{ effect: string }>('PUT', `${base(projectId)}/personal-links`, link);

export const deletePersonalLink = (projectId: string, link: { frontend: string; name: string }) =>
  call<{ effect: string }>(
    'DELETE',
    `${base(projectId)}/personal-links?frontend=${encodeURIComponent(link.frontend)}&name=${encodeURIComponent(link.name)}`,
  );

/** A file as a data URL, for upload. */
export const readAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsDataURL(file);
  });

/**
 * Whether this browser can mark up another application in place. Only the Chrome
 * extension can, on a desktop (docs/ui-tab.md §3); a phone or tablet browser can mark
 * up an uploaded screenshot, and is told why it can do no more.
 */
export const isTouchDevice = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(pointer: coarse)').matches === true ||
    // An iPad asking for the desktop site reports itself as a Mac with a touch screen.
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));
